import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { authClient } from '../api.js';
import { useI18n } from '../i18n.js';
import { afterLoginTarget } from '../oauth.js';
import { Icon } from '../ui.js';

export function Login() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(params.get('error'));
  const config = useQuery({
    queryKey: ['config'],
    queryFn: async () => (await fetch('/api/config')).json() as Promise<{ microsoft: boolean }>,
  });
  const target = afterLoginTarget(window.location.search);
  const errorURL = `/login?${new URLSearchParams({ ...(params.get('next') ? { next: params.get('next')! } : {}), error: 'signin' })}`;

  async function sendLink(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const res = await authClient.signIn.magicLink({
      email: email.trim(),
      callbackURL: target,
      errorCallbackURL: errorURL,
    });
    setBusy(false);
    if (res.error) setError(res.error.message ?? 'error');
    else setSent(true);
  }

  async function microsoft() {
    setError(null);
    const res = await authClient.signIn.social({
      provider: 'microsoft',
      callbackURL: target,
      errorCallbackURL: errorURL,
    });
    if (res.error) setError(res.error.message ?? 'error');
  }

  const forInvite = params.get('next')?.startsWith('/invite/');
  return (
    <div className="stack login">
      {forInvite && !sent && <p className="note">{t('loginForInvite')}</p>}
      {error && (
        <p className="error" role="alert">
          {t('signInError')}
          {error !== 'signin' ? ` (${error})` : ''}
        </p>
      )}
      {sent ? (
        <div className="stack">
          <p className="sent">{t('linkSent')}</p>
          <p className="muted">{email.trim()}</p>
          <div>
            <button className="quiet flush" onClick={() => setSent(false)}>
              {t('otherEmail')}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={sendLink} className="stack">
          <input
            type="email"
            required
            autoFocus
            aria-label={t('email')}
            placeholder={t('email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
          <button className="primary block" disabled={busy}>
            {t('sendLink')}
          </button>
        </form>
      )}
      {config.data?.microsoft && !sent && (
        <>
          <hr className="rule" />
          <button className="block" onClick={microsoft}>
            <Icon.Microsoft />
            {t('microsoft')}
          </button>
        </>
      )}
    </div>
  );
}
